import { Controller } from "@hotwired/stimulus"

// One document line's "Charge to" picker: a project, a job card, or a general
// cost for a division ("Division:workshop"). Keeps the rest of the line in step
// so nobody is told no on submit:
//   - the expense category is only asked for (and only sent) on a project/job
//   - a short hint says what the line will do, and warns when a project/job
//     cost account (5000-series, `data-direct` on its option) names no job.
export default class extends Controller {
  static targets = ["account", "chargeTo", "category", "categoryField", "hint"]
  // How an unneeded category field is put away. "invisible" keeps its slot, so
  // a table column or a grid row does not jump; a stacked layout can collapse
  // it instead ("hidden").
  static values = { hideClasses: { type: String, default: "" } }

  connect() {
    this.update()
  }

  update() {
    const value = this.chargeToTarget.value
    const job = /^(Project|WorkshopJobCard):/.test(value)
    const general = value.startsWith("Division:")
    const account = this.hasAccountTarget ? this.accountTarget.selectedOptions[0] : null
    const accountChosen = Boolean(account && account.value)
    const direct = account?.dataset.direct === "true"
    const overhead = account?.dataset.overhead === "true"

    if (this.hasCategoryTarget) {
      this.categoryTarget.disabled = !job
      this.categoryTarget.required = job
      if (!job) this.categoryTarget.value = ""
      // Always on screen, so nobody hunts for it; the blank option says why it is off.
      const blank = this.categoryTarget.querySelector("option[value='']")
      if (blank) blank.textContent = job ? "— choose category —" : "Only for a project / job"
    }
    if (this.hasCategoryFieldTarget) {
      this.hideClassesValue.split(/\s+/).filter(Boolean)
        .forEach((name) => this.categoryFieldTarget.classList.toggle(name, !job))
    }

    if (!this.hasHintTarget) return

    const divisionAllowed = account?.dataset.divisionAllowed === "true"

    if (direct && !job && !(general && divisionAllowed)) {
      this.hint(divisionAllowed
        ? "This is a project / job cost account — choose a project, a job card or a division."
        : "This is a project / job cost account — choose the project or job card it belongs to.", "warn")
    } else if (job) {
      this.hint("Costed to this job — choose the expense category.", "info")
    } else if (general) {
      const label = this.chargeToTarget.selectedOptions[0]?.text.replace(/\s*\(general\)\s*$/, "").trim()
      this.hint(`General cost — counts in ${label} overheads.`, "info")
    } else if (overhead) {
      this.hint("No division chosen — this general cost will count as Head Office.", "muted")
    } else if (accountChosen) {
      this.hint("Stock / asset line — no project needed.", "muted")
    } else {
      this.hint("", "muted")
    }
  }

  hint(text, tone) {
    const el = this.hintTarget
    el.textContent = text
    el.dataset.tone = tone
    el.classList.toggle("text-amber-700", tone === "warn")
    el.classList.toggle("dark:text-amber-400", tone === "warn")
    el.classList.toggle("text-slate-500", tone !== "warn")
    el.classList.toggle("dark:text-slate-400", tone !== "warn")
  }
}
